import React from 'react';
import { useIntl } from 'react-intl';
import { Info, Lock } from 'lucide-react';

import { AddOn } from 'store/resource/types';
import { PartySection, UNATTRIBUTED_PARTY_KEY } from '../group-by-party';
import { AddOnItem } from './add-on-item';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AddOnCatalog } from '../components/add-on-catalog';

export interface AddOnsPickerProps {
  partySections: Array<PartySection>;
  isPartyLocked: (partyKey: string) => boolean;
  isKept?: (addOn: AddOn) => boolean;
  quantityOf: (addOnId: string) => number;
  minQuantityOf: (addOnId: string) => number;
  isAtMax: (addOnId: string) => boolean;
  isFixedQuantity: (addOnId: string) => boolean;
  isAwaitingRanges: boolean;
  onIncrease: (addOn: AddOn) => void;
  onDecrease: (addOn: AddOn) => void;
  onRemove: (addOn: AddOn) => void;
  expandedId: string | null;
  onToggle: (addOnId: string) => void;
  /* Catalog items whose owner has no instance at this building. Surfaced, never blocking. */
  unresolvedAddOns: Array<AddOn>;
  /* RES-1359. Dates each row's lead-time line. */
  bookingStart?: string;
}

/**
 * RES-1367 — the add-on catalogue, as the my-bookings dialog draws it.
 *
 * ⛔ NO HEADING OF ITS OWN. The dialog header above already says "Add-ons", and a second title a few
 * pixels under it is the duplication the sheet version already had to remove. Each provider block is
 * still a heading, so the surface keeps its structure for a screen reader.
 *
 * ⚠️ `role="group"` per party, never a <ul>/<li>: `aria-disabled` on a listitem is an eslint error in
 * this config, and a locked row has to announce the party it belongs to.
 */
export const AddOnsPicker = ({
  partySections,
  isPartyLocked,
  isKept = () => false,
  quantityOf,
  minQuantityOf,
  isAtMax,
  isFixedQuantity,
  isAwaitingRanges,
  onIncrease,
  onDecrease,
  onRemove,
  expandedId,
  onToggle,
  unresolvedAddOns,
  bookingStart,
}: AddOnsPickerProps): JSX.Element => {
  const intl = useIntl();
  /* VIS-2193. The image-led add-on cards, behind `show-product-images`. Off: the rows below, as before. */
  const { showProductImages } = useFlags();

  return (
    <div className="flex flex-col gap-6" data-testid="add-ons-picker">
      {showProductImages ? (
        <AddOnCatalog
          partySections={partySections}
          isPartyLocked={isPartyLocked}
          isKept={isKept}
          quantityOf={quantityOf}
          minQuantityOf={minQuantityOf}
          isAtMax={isAtMax}
          isFixedQuantity={isFixedQuantity}
          isAwaitingRanges={isAwaitingRanges}
          onIncrease={onIncrease}
          onDecrease={onDecrease}
          onRemove={onRemove}
          expandedId={expandedId}
          onToggle={onToggle}
          bookingStart={bookingStart}
          /* The dialog's own title is the h2. */
          headingLevel="h3"
          testIdPrefix="add-ons-picker-catalog"
        />
      ) : (
        partySections.map(section => {
          const isLocked = isPartyLocked(section.key);
          const headingId = `add-ons-picker-party-${section.key || UNATTRIBUTED_PARTY_KEY || 'unattributed'}`;

          return (
            <section
              key={section.key}
              role="group"
              /*
               * Named only when there IS a heading: the "Add-ons" fallback collides verbatim with the
               * dialog's own title, and an unnamed group beats a misleadingly-named one.
               */
              aria-labelledby={section.name ? headingId : undefined}
              /*
               * ⛔ NOT A CARD. A bordered box per vendor, inside a bordered dialog, around bordered rows
               * was three nested frames for one list -- the shapes competed and nothing read as the
               * thing you were choosing from. A label, a rule under it and the rows below carry the
               * same grouping with none of the furniture.
               */
              className="px-0.5"
              data-testid={`add-ons-picker-party-${section.key || 'unattributed'}`}
            >
              {section.name && (
                <h2 className="mb-1 flex items-center gap-2 border-b border-border pb-2">
                  <span id={headingId} className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                    {section.name}
                  </span>
                  {isLocked && (
                    <span
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                      data-testid={`add-ons-picker-party-locked-${section.key}`}
                    >
                      <Lock className="size-3" aria-hidden="true" />
                      {intl.formatMessage({ id: 'add_on_party_locked' })}
                    </span>
                  )}
                </h2>
              )}
              <div className="divide-y divide-border">
                {section.addOns.map(addOn => (
                  <AddOnItem
                    key={addOn.id}
                    addOn={addOn}
                    quantity={quantityOf(addOn.id)}
                    onIncrease={onIncrease}
                    onDecrease={onDecrease}
                    onRemove={onRemove}
                    isExpanded={expandedId === addOn.id}
                    onToggle={onToggle}
                    isPartyLocked={isLocked}
                    isKept={isKept(addOn)}
                    isFixedQuantity={isFixedQuantity(addOn.id)}
                    isAtMax={isAtMax(addOn.id)}
                    isAwaitingRanges={isAwaitingRanges}
                    minQuantity={minQuantityOf(addOn.id)}
                    bookingStart={bookingStart}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}

      {/*
        An item returned in NO owner's group has an owner with no instance at this building — exactly
        what the Service Request create rejects on. ⚠️ WARN, DO NOT BLOCK: the tenant has already paid
        for the room and the fix is an admin's to make, so blocking submit would strand them behind a
        condition they cannot clear. Naming the item lets them remove it.
      */}
      {unresolvedAddOns.length > 0 && (
        <p
          role="status"
          className="flex items-start gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground"
          data-testid="add-ons-picker-unresolved"
        >
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>
            {intl.formatMessage(
              { id: 'add_on_unavailable_here' },
              { items: unresolvedAddOns.map(addOn => addOn.name).join(', ') },
            )}
          </span>
        </p>
      )}
    </div>
  );
};
